import { Css, Icon, Palette, useTestIds } from "@homebound/beam";
import { Maybe, TradePartner } from "src/generated/graphqlTypes";
import { pluralize } from "src/utils";

const onlyOneTrade = "Trade comms only support one trade per task.";
const listFormat = new Intl.ListFormat("en-US", { style: "long", type: "conjunction" });

type OtherCommittedTradesWarningProps = {
  tradePartner?: Maybe<Pick<TradePartner, "id" | "name">>;
  committedTradePartners: Pick<TradePartner, "id" | "name">[];
  /** `inline` shows the message under a task in flows that send trade comms. `tooltip` shows an icon in status views. */
  variant: "inline" | "tooltip";
};

/**
 * Warns that trade comms only go to the task's trade partner when other trades also have signed commitments on the task.
 * Renders nothing when the task has a single trade.
 */
export function OtherCommittedTradesWarning({
  tradePartner,
  committedTradePartners,
  variant,
}: OtherCommittedTradesWarningProps) {
  const tid = useTestIds({}, "otherCommittedTradesWarning");
  const otherTrades = getOtherCommittedTrades({ tradePartner, committedTradePartners });
  if (!tradePartner || otherTrades.isEmpty) return null;

  const otherTradeNames = listFormat.format(otherTrades.map((tp) => tp.name));

  if (variant === "tooltip") {
    const message = `${onlyOneTrade} This status applies to ${tradePartner.name}. ${otherTradeNames} ${pluralize(otherTrades, "isn't", "aren't")} included.`;
    return (
      <span css={Css.df.fs0.$} {...tid}>
        <Icon icon="error" color={Palette.Red600} tooltip={message} inc={2.3} />
      </span>
    );
  }

  const commitment = pluralize(otherTrades, "also has a signed commitment", "also have signed commitments");
  return (
    <div css={Css.df.gap1.sm.red600.tal.$} {...tid}>
      <Icon icon="error" color={Palette.Red600} inc={2} xss={Css.fs0.$} />
      <span>{`${onlyOneTrade} ${otherTradeNames} ${commitment} and won't be notified, so contact them directly.`}</span>
    </div>
  );
}

/** Lets callers skip wrapper markup when there is no warning to show */
export function hasOtherCommittedTrades(
  task: Pick<OtherCommittedTradesWarningProps, "tradePartner" | "committedTradePartners">,
) {
  return !!task.tradePartner && getOtherCommittedTrades(task).nonEmpty;
}

function getOtherCommittedTrades({
  tradePartner,
  committedTradePartners,
}: Pick<OtherCommittedTradesWarningProps, "tradePartner" | "committedTradePartners">) {
  return committedTradePartners.uniqueBy((tp) => tp.id).filter((tp) => tp.id !== tradePartner?.id);
}
